import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, SITE } from "@/lib/site";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [introSeen] = useState(() =>
    typeof window !== "undefined" && sessionStorage.getItem("scharpf_intro_seen")
  );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: introSeen ? 0 : 2.6, duration: 0.6 }}
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/85 border-b border-slate-200 shadow-sm"
          : "bg-white/60 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 flex items-center justify-between h-20">
        <a href="#top" data-testid="nav-logo" className="flex items-center gap-3 group">
          <div className="w-14 h-14 rounded-2xl bg-white shadow-md ring-1 ring-slate-200 flex items-center justify-center p-1.5 group-hover:scale-105 transition-transform">
            <img
              src={SITE.logo}
              alt="Scharpf Logo"
              className="w-full h-full object-contain"
            />
          </div>
          <div className="hidden sm:block leading-tight">
            <div className="font-heading text-navy text-lg font-extrabold tracking-tight">Scharpf</div>
            <div className="text-[11px] text-slate-500 uppercase tracking-[0.18em]">
              Haushaltsauflösungen
            </div>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.replace("#", "")}`}
              className="px-4 py-2 rounded-full text-sm font-semibold text-navy hover:bg-slate-100 transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={`tel:${SITE.phoneE164}`}
            data-testid="nav-call-button"
            className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-navy hover:bg-slate-100 transition-colors"
          >
            <Phone className="w-4 h-4" />
            {SITE.phoneDisplay}
          </a>
          <a
            href="#kontakt"
            data-testid="nav-cta-button"
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-green-accent text-white text-sm font-bold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
            style={{ backgroundColor: "#4ea83a" }}
          >
            Kostenlose Besichtigung
          </a>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((s) => !s)}
            className="md:hidden p-2 rounded-full text-navy hover:bg-slate-100"
            aria-label="Menü"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t border-slate-200" data-testid="mobile-menu">
          <div className="px-5 py-4 flex flex-col gap-2">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`mobile-nav-link-${l.href.replace("#", "")}`}
                onClick={() => setOpen(false)}
                className="px-4 py-3 rounded-2xl text-base font-semibold text-navy hover:bg-slate-100"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#kontakt"
              data-testid="mobile-nav-cta"
              onClick={() => setOpen(false)}
              className="px-5 py-3 rounded-2xl bg-green-accent text-white text-center font-bold mt-2"
              style={{ backgroundColor: "#4ea83a" }}
            >
              Kostenlose Besichtigung
            </a>
          </div>
        </div>
      )}
    </motion.header>
  );
}
